import { Component, h, Prop } from '@stencil/core';
import { select, line, scaleSequential, scaleLinear, extent, axisBottom, format } from 'd3';

const getPositionRelativeToContainer = (point, containerDimension) => (point * 100) / containerDimension;
@Component({
  tag: 'hd-chart',
  styleUrl: './hd-chart.pcss',
  shadow: true,
})
export class HdChart {
  @Prop() data: string = '';
  @Prop() unit: string = 'million';
  @Prop() customLabel: string = 'Monthly Visits';

  container!: HTMLElement;
  svg!: HTMLOrSVGElement;
  parsedData = [];

  connectedCallback() {
    try {
      this.parsedData = JSON.parse(this.data);
    } catch (e) {
      console.error('[HADID] Error parsing data for Chart Component', e);
    }
  }

  componentDidRender() {
    this._initChart(this.parsedData);
  }

  _initChart(data) {
    const containerWidth = 1040;
    const containerHeight = 500;

    const marginTop = 120;
    const marginBottom = 24;
    const marginLeft = 80;
    const marginRight = 16;
    const marginX = marginLeft + marginRight;
    const marginY = marginTop + marginBottom;
    const width = containerWidth - marginX;
    const height = containerHeight - marginY;

    const svg = select(this.svg).attr('viewBox', `0 0 ${containerWidth} ${containerHeight}`);

    const container = select(this.container).style('position', 'relative');

    const [min, max] = extent(data, d => d.year);
    const xScale = scaleSequential().domain([min, max]).range([0, width]);

    const yScale = scaleLinear()
      .domain(extent(data, d => d.value))
      .range([height, 0]);

    const xAxis = axisBottom(xScale).tickFormat(format('d')).tickPadding(4);

    const axis = svg
      .append('g')
      .attr('transform', `translate(${marginLeft}, ${height + marginTop})`)
      .call(xAxis);

    axis.selectAll('line').remove();
    axis.select('.domain').remove();
    axis.selectAll('.tick').append('rect').lower().style('fill', '#FFFFFF').attr('width', 40).attr('height', 20).attr('transform', 'translate(-20, 5)');

    axis.selectAll('text').style('fill', '#BBBBBB').style('font-family', 'var(--body-font)').style('font-size', '13px').style('font-weight', '600');

    // fake axis, aligned with ticks
    axis.append('line').lower().attr('x1', 0).attr('y1', 15).attr('x2', width).attr('y2', 15).attr('stroke', '#BBBBBB').attr('stroke-width', 1);

    const chart = svg.append('g').attr('transform', `translate(${marginLeft}, ${marginTop})`);

    // line
    chart
      .append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', 'var(--primary)')
      .attr('stroke-width', 1)
      .attr(
        'd',
        line()
          .x(d => xScale(d.year))
          .y(d => yScale(d.value))
      );

    // line from point to bottom axis
    chart
      .selectAll('line')
      .data(data)
      .enter()
      .append('line')
      .attr('x1', d => xScale(d.year))
      .attr('y1', d => yScale(d.value))
      .attr('x2', d => xScale(d.year))
      .attr('y2', height)
      .attr('stroke', '#E4E4E4')
      .style('opacity', '.35');

    // dots
    chart
      .selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('r', 4.5)
      .attr('cx', d => xScale(d.year))
      .attr('cy', d => yScale(d.value))
      .attr('fill', 'var(--primary)');

    // legend
    const LINE_HEIGHT = 44;

    // line from point to label
    chart
      .append('g')
      .lower()
      .selectAll('line')
      .data(data)
      .enter()
      .append('line')
      .attr('x1', d => xScale(d.year))
      .attr('y1', d => yScale(d.value) - LINE_HEIGHT)
      .attr('x2', d => xScale(d.year))
      .attr('y2', d => yScale(d.value))
      .attr('stroke', '#E4E4E4');

    const texts = container.append('div')
      .attr('class', 'labels')
      .style('transform', `translate(${marginLeft}, ${marginTop})`)
      .selectAll('.chart-label')
      .data(data)
      .enter();

    // position of text needs to be a percentage so that it's responsive
    texts
      .append('div')
      .attr('class', 'chart-label')
      .style('position', 'absolute')
      .style('top', d => {
        const positionPx = yScale(d.value) + marginTop - LINE_HEIGHT - 10;
        const positionPercentage = getPositionRelativeToContainer(positionPx, containerHeight);
        return `${positionPercentage}%`;
      })
      .style('left', d => {
        const positionPx = xScale(d.year) + marginLeft;
        const positionPercentage = getPositionRelativeToContainer(positionPx, containerWidth);
        return `${positionPercentage}%`;
      })
      .style('transform', 'translate(-100%, -100%)')
      .style('width', `calc(100% / ${data.length - 1} - 16px)`)
      .html(
        d => `<p class="value">${d.value} <span>${this.unit}</span></p>
        <p class="type">${this.customLabel}</p>`
      )
      .attr('fill', 'rgb(94, 149, 199)');
  }

  render() {
    return (
      <div class="chart-container" ref={el => (this.container = el as HTMLElement)}>
        <svg ref={el => (this.svg = el as SVGElement)}></svg>
      </div>
    );
  }
}
